import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaTrash, FaSignOutAlt, FaList, FaEye, FaUpload, FaImage, FaPrint, FaDownload, FaTools, FaEdit } from 'react-icons/fa';
import SEO from '../components/SEO';
import { validateCertificateId, CERTIFICATE_ID_HINT } from '../utils/certificateId';
import '../styles/admin-dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const today = new Date().toISOString().split('T')[0];

const emptyRecipient = () => ({ certificateId: '', recipientName: '', recipientEmail: '' });

const initialSharedCertificateData = {
  certificateTitle: '',
  description: '',
  certificateType: 'Course Completion',
  issuedDate: today,
  expiryDate: '',
  grade: 'A',
  skills: ''
};

const defaultEquipmentRows = [
  { description: 'VISUAL INSPECTION', observation: 'GENERALLY LOOKING GOOD', remarks: 'SATISFACTORY' },
  { description: 'INLET VALVE', observation: 'IN GOOD WORKING CONDITION', remarks: 'SATISFACTORY' },
  { description: 'OUTLET VALVE', observation: 'IN GOOD WORKING CONDITION', remarks: 'SATISFACTORY' },
  { description: 'OTHER ATTACHMENTS', observation: 'IN GOOD CONDITION AND FUNCTIONAL', remarks: 'SATISFACTORY' }
];

const defaultEquipmentCertificate = {
  client: 'NATONY LIMITED',
  clientLocation: 'NLNG LAY DOWN AREA BONNY, RIVERS STATE',
  testLocation: 'NLNG LAY DOWN AREA BONNY, RIVERS STATE',
  itemDescription: 'AIR MANIFOLD',
  equipmentId: 'NATL/AM/001',
  certificateNumber: 'DOL/NAT/ AM/01',
  maxWorkingPressure: '10 BAR',
  temperature: '28.5 +/-2',
  relativeHumidity: '68% +5%',
  testedDate: '2026-09-11',
  nextTestDate: '2027-03-10',
  validity: '6 MONTHS',
  procedureNo: 'DOL-TTS-002',
  referenceStandard: 'ASME B 31:3, ISO 5208: 2015',
  inspectorName: 'Akaniyene U.',
  inspectorCredential: 'ML/FID/CP/20/103',
  recipientEmail: '',
  testRows: defaultEquipmentRows
};

export default function AdminDashboard() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showEquipmentForm, setShowEquipmentForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [previewCert, setPreviewCert] = useState(null);
  const [printCert, setPrintCert] = useState(null);
  const [editingCert, setEditingCert] = useState(null);
  const [showCertificationUpload, setShowCertificationUpload] = useState(false);
  const [certificationFile, setCertificationFile] = useState(null);
  const [certificationImageUrl, setCertificationImageUrl] = useState(null);
  const [uploadingCertification, setUploadingCertification] = useState(false);
  const [certificateImageFile, setCertificateImageFile] = useState(null);
  const [equipmentData, setEquipmentData] = useState(defaultEquipmentCertificate);
  const [equipmentImageAttachment, setEquipmentImageAttachment] = useState(null);
  const [creatingEquipmentCertificate, setCreatingEquipmentCertificate] = useState(false);
  const [editImageFile, setEditImageFile] = useState(null);
  const [updatingCertificate, setUpdatingCertificate] = useState(false);
  
  const [formData, setFormData] = useState(initialSharedCertificateData);
  const [recipients, setRecipients] = useState([emptyRecipient()]);
  const [creatingCertificates, setCreatingCertificates] = useState(false);

  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  // Validation regex patterns
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    const seenCertificateIds = new Map();

    recipients.forEach((recipient, index) => {
      const idCheck = validateCertificateId(recipient.certificateId);
      if (!idCheck.isValid) {
        newErrors[`recipient.${index}.certificateId`] = idCheck.error;
      } else if (seenCertificateIds.has(idCheck.value)) {
        newErrors[`recipient.${index}.certificateId`] =
          `Duplicate of recipient ${seenCertificateIds.get(idCheck.value) + 1}. Each recipient needs a unique certificate ID`;
      } else {
        seenCertificateIds.set(idCheck.value, index);
      }

      if (!recipient.recipientName.trim()) {
        newErrors[`recipient.${index}.recipientName`] = 'Recipient name is required';
      } else if (recipient.recipientName.trim().length < 2) {
        newErrors[`recipient.${index}.recipientName`] = 'Name must be at least 2 characters';
      }

      if (!recipient.recipientEmail.trim()) {
        newErrors[`recipient.${index}.recipientEmail`] = 'Recipient email is required';
      } else if (!emailRegex.test(recipient.recipientEmail.trim())) {
        newErrors[`recipient.${index}.recipientEmail`] = 'Please enter a valid email address';
      }
    });

    if (!formData.certificateTitle.trim()) {
      newErrors.certificateTitle = 'Certificate title is required';
    } else if (formData.certificateTitle.trim().length < 3) {
      newErrors.certificateTitle = 'Title must be at least 3 characters';
    }

    if (!formData.issuedDate) {
      newErrors.issuedDate = 'Issue date is required';
    }

    if (formData.expiryDate && formData.issuedDate && new Date(formData.expiryDate) <= new Date(formData.issuedDate)) {
      newErrors.expiryDate = 'Expiry date must be after issue date';
    }

    if (formData.description && formData.description.trim().length < 5) {
      newErrors.description = 'Description must be at least 5 characters';
    }

    return newErrors;
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) navigate('/admin/login'); 
    else fetchCertificates();
  }, [navigate]);

  const fetchCertificates = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/certificates/company`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (response.data.success) setCertificates(response.data.certificates);
    } catch (err) { 
      setError('Failed to fetch certificates'); 
      console.error(err); 
    } finally { 
      setLoading(false); 
    }
  };

  const buildCertificateVerificationUrl = (cert) => {
    if (!cert) return '';

    const baseUrl = window.location.origin || 'http://localhost:3000';
    const certificateId = encodeURIComponent(cert.certificateId || '');
    const params = new URLSearchParams({
      certificateId: cert.certificateId || '',
      recipient: cert.recipientName || '',
      title: cert.certificateTitle || '',
      issuer: cert.issuerName || ''
    });

    return `${baseUrl}/verify/${certificateId}?${params.toString()}`;
  };

  const getCertificatePrintQrUrl = (cert) => {
    if (!cert) return '';
    if (cert.qrCode) return cert.qrCode;

    const verificationUrl = buildCertificateVerificationUrl(cert);
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(verificationUrl)}`;
  };

  const handlePrintCertificate = (cert) => {
    setShowPreview(false);
    setPrintCert(cert);
    setShowPrintPreview(true);
  };

  const handlePrintCurrentCertificate = () => {
    requestAnimationFrame(() => {
      window.print();
    });
  };

  const loadImageForCanvas = (src) => new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('Image source is missing'));
      return;
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Unable to load the certificate image for download'));
    image.src = src;
  });

  const [editFormData, setEditFormData] = useState({
    certificateId: '',
    recipientName: '',
    recipientEmail: '',
    certificateTitle: '',
    description: '',
    certificateType: 'Certification',
    issuedDate: today,
    expiryDate: '',
    grade: '',
    skills: ''
  });

  const downloadCertificateImage = async (cert, format = 'png') => {
    if (!cert?.certificateImage) {
      setError('This certificate does not have an image to download');
      return;
    }

    const mmToPx = 10;
    const canvas = document.createElement('canvas');
    canvas.width = 297 * mmToPx;
    canvas.height = 210 * mmToPx;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    try {
      const [certSource, qrSource] = await Promise.all([
        loadImageForCanvas(cert.certificateImage),
        loadImageForCanvas(getCertificatePrintQrUrl(cert))
      ]);

      if (format === 'jpeg') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(certSource, 0, 0, canvas.width, canvas.height);

      const marginRight = 17 * mmToPx;
      const marginBottom = 15 * mmToPx;
      const qrSize = 25 * mmToPx;
      const qrX = canvas.width - marginRight - qrSize;
      const qrY = canvas.height - marginBottom - qrSize;

      ctx.drawImage(qrSource, qrX, qrY, qrSize, qrSize);

      ctx.fillStyle = '#0f172a';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.font = '700 16px Arial';
      ctx.fillText('SCAN QR CODE', qrX + qrSize / 2, qrY + qrSize + 6);
      ctx.font = '700 15px Arial';
      ctx.fillText('FOR VERIFICATION', qrX + qrSize / 2, qrY + qrSize + 28);

      const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
      const extension = format === 'jpeg' ? 'jpeg' : 'png';
      const link = document.createElement('a');
      link.download = `${(cert.certificateId || 'certificate').replace(/[^\w.-]+/g, '-')}.${extension}`;
      link.href = canvas.toDataURL(mimeType, 0.95);
      link.click();
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to download certificate image');
    }
  };

  const exportPrintCertificateImage = async (format = 'png') => {
    await downloadCertificateImage(printCert, format);
  };

  const handleInputChange = (e) => { 
    const { name, value } = e.target; 
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const clearFieldError = (key) => {
    if (errors[key]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const handleRecipientChange = (index, field, value) => {
    setRecipients(prev => prev.map((recipient, rowIndex) => (
      rowIndex === index ? { ...recipient, [field]: value } : recipient
    )));
    clearFieldError(`recipient.${index}.${field}`);
  };

  const addRecipient = () => {
    setRecipients(prev => [...prev, emptyRecipient()]);
  };

  const removeRecipient = (index) => {
    setRecipients(prev => (prev.length > 1 ? prev.filter((_, rowIndex) => rowIndex !== index) : prev));
    setErrors(prev => {
      const newErrors = {};
      Object.keys(prev).forEach(key => {
        if (!key.startsWith('recipient.')) newErrors[key] = prev[key];
      });
      return newErrors;
    });
  };

  const handleEquipmentInputChange = (e) => {
    const { name, value } = e.target;
    setEquipmentData(prev => ({ ...prev, [name]: value }));
    if (errors[`equipment.${name}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`equipment.${name}`];
        return newErrors;
      });
    }
  };

  const handleEquipmentRowChange = (index, field, value) => {
    setEquipmentData(prev => ({
      ...prev,
      testRows: prev.testRows.map((row, rowIndex) => (
        rowIndex === index ? { ...row, [field]: value } : row
      ))
    }));
    if (errors[`equipment.row${index}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`equipment.row${index}`];
        return newErrors;
      });
    }
  };

  const handleEquipmentImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      setEquipmentImageAttachment(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid equipment image file');
      return;
    }

    if (file.size > 5242880) {
      setError('Equipment image file size exceeds 5MB limit');
      return;
    }

    setEquipmentImageAttachment(file);
    if (errors['equipment.certificateImage']) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors['equipment.certificateImage'];
        return newErrors;
      });
    }
    setError('');
  };

  const validateEquipmentForm = () => {
    const requiredFields = [
      ['client', 'Client is required'],
      ['clientLocation', 'Client location is required'],
      ['testLocation', 'Location of tests is required'],
      ['itemDescription', 'Item description is required'],
      ['equipmentId', 'ID number is required'],
      ['maxWorkingPressure', 'Maximum working pressure is required'],
      ['testedDate', 'Test date is required'],
      ['nextTestDate', 'Next test date is required'],
      ['validity', 'Validity is required'],
      ['procedureNo', 'Procedure number is required'],
      ['referenceStandard', 'Reference standard is required']
    ];
    const newErrors = {};

    requiredFields.forEach(([field, message]) => {
      if (!equipmentData[field]?.trim()) {
        newErrors[`equipment.${field}`] = message;
      }
    });

    const certificateNumberCheck = validateCertificateId(equipmentData.certificateNumber);
    if (!certificateNumberCheck.isValid) {
      newErrors['equipment.certificateNumber'] = certificateNumberCheck.error.replace('Certificate ID', 'Certificate number');
    }

    if (equipmentData.recipientEmail && !emailRegex.test(equipmentData.recipientEmail)) {
      newErrors['equipment.recipientEmail'] = 'Please enter a valid email address';
    }

    if (
      equipmentData.nextTestDate &&
      equipmentData.testedDate &&
      new Date(equipmentData.nextTestDate) <= new Date(equipmentData.testedDate)
    ) {
      newErrors['equipment.nextTestDate'] = 'Next test date must be after the test date';
    }

    equipmentData.testRows.forEach((row, index) => {
      if (!row.description.trim() || !row.observation.trim() || !row.remarks.trim()) {
        newErrors[`equipment.row${index}`] = 'Complete description, observation, and remarks';
      }
    });

    if (!equipmentImageAttachment) {
      newErrors['equipment.certificateImage'] = 'Equipment certification image is required';
    }

    return newErrors;
  };

  const toDateInputValue = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
  };

  const validateEditForm = () => {
    const newErrors = {};

    const editIdCheck = validateCertificateId(editFormData.certificateId);
    if (!editIdCheck.isValid) {
      newErrors.editCertificateId = editIdCheck.error;
    }

    if (!editFormData.recipientName.trim()) {
      newErrors.editRecipientName = 'Recipient name is required';
    } else if (editFormData.recipientName.trim().length < 2) {
      newErrors.editRecipientName = 'Name must be at least 2 characters';
    }

    if (editFormData.recipientEmail && !emailRegex.test(editFormData.recipientEmail)) {
      newErrors.editRecipientEmail = 'Please enter a valid email address';
    }

    if (!editFormData.certificateTitle.trim()) {
      newErrors.editCertificateTitle = 'Certificate title is required';
    } else if (editFormData.certificateTitle.trim().length < 3) {
      newErrors.editCertificateTitle = 'Title must be at least 3 characters';
    }

    if (!editFormData.issuedDate) {
      newErrors.editIssuedDate = 'Issue date is required';
    }

    if (
      editFormData.expiryDate &&
      editFormData.issuedDate &&
      new Date(editFormData.expiryDate) <= new Date(editFormData.issuedDate)
    ) {
      newErrors.editExpiryDate = 'Expiry date must be after issue date';
    }

    if (editFormData.description && editFormData.description.trim().length < 5) {
      newErrors.editDescription = 'Description must be at least 5 characters';
    }

    return newErrors;
  };

  const handleEquipmentSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const newErrors = validateEquipmentForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setError('Please correct the errors highlighted in the equipment form');
      return;
    }

    setCreatingEquipmentCertificate(true);

    try {
      const token = localStorage.getItem('adminToken');
      const uploadFormData = new FormData();
      uploadFormData.append('certificateId', equipmentData.certificateNumber.trim());
      uploadFormData.append('recipientName', equipmentData.client);
      uploadFormData.append('recipientEmail', equipmentData.recipientEmail);
      uploadFormData.append('certificateTitle', `${equipmentData.itemDescription} Equipment Certification`);
      uploadFormData.append('description', `Equipment ID: ${equipmentData.equipmentId}. Location of tests: ${equipmentData.testLocation}. Max working pressure: ${equipmentData.maxWorkingPressure}. Procedure: ${equipmentData.procedureNo}. Reference standard: ${equipmentData.referenceStandard}.`);
      uploadFormData.append('certificateType', 'Equipment Certification');
      uploadFormData.append('issuedDate', new Date(equipmentData.testedDate).toISOString());
      uploadFormData.append('expiryDate', new Date(equipmentData.nextTestDate).toISOString());
      uploadFormData.append('issuerName', 'DOFCAN OILSERVE LTD');
      uploadFormData.append('grade', 'SATISFACTORY');
      uploadFormData.append('skills', equipmentData.testRows.map(row => row.description).join(', '));
      uploadFormData.append('certificateImage', equipmentImageAttachment);

      const response = await axios.post(`${API_URL}/certificates/create`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSuccess('Equipment certification created successfully!');
        setPreviewCert(response.data.certificate);
        setShowPreview(true);
        setShowEquipmentForm(false);
        setEquipmentData({
          ...defaultEquipmentCertificate,
          testRows: defaultEquipmentRows.map(row => ({ ...row }))
        });
        setEquipmentImageAttachment(null);
        setErrors({});
        fetchCertificates();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error creating equipment certification');
    } finally {
      setCreatingEquipmentCertificate(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5242880) {
        setError('File size exceeds 5MB limit');
        return;
      }
      setCertificateImageFile(file);
      if (errors.certificateImage) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.certificateImage;
          return newErrors;
        });
      }
      setError('');
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
    const errorKey = `edit${name.charAt(0).toUpperCase()}${name.slice(1)}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      setEditImageFile(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    if (file.size > 5242880) {
      setError('File size exceeds 5MB limit');
      return;
    }

    setEditImageFile(file);
    setError('');
  };

  const handleEditCertificate = (cert) => {
    setEditingCert(cert);
    setEditImageFile(null);
    setEditFormData({
      certificateId: cert.certificateId || '',
      recipientName: cert.recipientName || '',
      recipientEmail: cert.recipientEmail || '',
      certificateTitle: cert.certificateTitle || '',
      description: cert.description || '',
      certificateType: cert.certificateType || 'Certification',
      issuedDate: toDateInputValue(cert.issuedDate) || today,
      expiryDate: toDateInputValue(cert.expiryDate),
      grade: cert.grade || '',
      skills: Array.isArray(cert.skills) ? cert.skills.join(', ') : (cert.skills || '')
    });
    setShowPreview(false);
    setShowPrintPreview(false);
    setShowEditForm(true);
    setErrors({});
    setError('');
  };

  const closeEditForm = () => {
    setShowEditForm(false);
    setEditingCert(null);
    setEditImageFile(null);
    setErrors({});
    setError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingCert?._id) return;

    setError('');
    setSuccess('');

    const newErrors = validateEditForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setError('Please correct the errors highlighted in the edit form');
      return;
    }

    setUpdatingCertificate(true);

    try {
      const token = localStorage.getItem('adminToken');
      const updateFormData = new FormData();
      updateFormData.append('certificateId', editFormData.certificateId.trim());
      updateFormData.append('recipientName', editFormData.recipientName);
      updateFormData.append('recipientEmail', editFormData.recipientEmail);
      updateFormData.append('certificateTitle', editFormData.certificateTitle);
      updateFormData.append('description', editFormData.description);
      updateFormData.append('certificateType', editFormData.certificateType);
      updateFormData.append('issuedDate', new Date(editFormData.issuedDate).toISOString());
      updateFormData.append('expiryDate', editFormData.expiryDate ? new Date(editFormData.expiryDate).toISOString() : '');
      updateFormData.append('grade', editFormData.grade);
      updateFormData.append('skills', editFormData.skills);

      if (editImageFile) {
        updateFormData.append('certificateImage', editImageFile);
      }

      const response = await axios.put(`${API_URL}/certificates/${editingCert._id}`, updateFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSuccess('Certificate updated successfully!');
        closeEditForm();
        fetchCertificates();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating certificate');
    } finally {
      setUpdatingCertificate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate form
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setError('Please correct the errors highlighted in the form');
      return;
    }

    setCreatingCertificates(true);
    const token = localStorage.getItem('adminToken');
    const created = [];
    const failed = [];

    // Shared details are sent once per recipient; each recipient gets its own record and ID.
    for (const recipient of recipients) {
      const uploadFormData = new FormData();
      uploadFormData.append('certificateId', recipient.certificateId.trim());
      uploadFormData.append('recipientName', recipient.recipientName.trim());
      uploadFormData.append('recipientEmail', recipient.recipientEmail.trim());
      uploadFormData.append('certificateTitle', formData.certificateTitle);
      uploadFormData.append('description', formData.description);
      uploadFormData.append('certificateType', formData.certificateType);
      uploadFormData.append('issuedDate', new Date(formData.issuedDate).toISOString());
      uploadFormData.append('expiryDate', formData.expiryDate);
      uploadFormData.append('grade', formData.grade);
      uploadFormData.append('skills', formData.skills);

      if (certificateImageFile) {
        uploadFormData.append('certificateImage', certificateImageFile);
      }

      try {
        const response = await axios.post(`${API_URL}/certificates/create`, uploadFormData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          created.push(response.data.certificate);
        } else {
          failed.push({ recipient, message: response.data.message || 'Error creating certificate' });
        }
      } catch (err) {
        failed.push({ recipient, message: err.response?.data?.message || 'Error creating certificate' });
      }
    }

    setCreatingCertificates(false);
    if (created.length > 0) fetchCertificates();

    if (failed.length === 0) {
      setSuccess(created.length === 1
        ? '✓ Certificate created successfully!'
        : `✓ ${created.length} certificates created successfully!`);
      setPreviewCert(created[0]);
      setShowPreview(true);
      setFormData(initialSharedCertificateData);
      setRecipients([emptyRecipient()]);
      setCertificateImageFile(null);
      setErrors({});
      setShowForm(false);
      setTimeout(() => setSuccess(''), 3000);
      return;
    }

    // Keep only the failed recipients in the form so they can be corrected and resubmitted.
    setRecipients(failed.map(item => item.recipient));
    setErrors({});
    const failureSummary = failed
      .map(item => `${item.recipient.certificateId.trim() || item.recipient.recipientName.trim() || 'Recipient'}: ${item.message}`)
      .join('; ');
    setError(created.length > 0
      ? `${created.length} created, ${failed.length} failed — ${failureSummary}`
      : failureSummary);
  };

  const handleDelete = async (id) => { 
    if (!window.confirm('Are you sure you want to delete this certificate?')) return; 
    try { 
      const token = localStorage.getItem('adminToken'); 
      const response = await axios.delete(`${API_URL}/certificates/${id}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      }); 
      if (response.data.success) { 
        setSuccess('✓ Certificate deleted successfully!'); 
        fetchCertificates(); 
        setTimeout(() => setSuccess(''), 3000); 
      } 
    } catch (err) { 
      setError('Failed to delete certificate'); 
    } 
  };

  const handleViewCertificate = (cert) => {
    setPreviewCert(cert);
    setShowPreview(true);
  };

  const handleCertificationFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5242880) {
        setError('File size exceeds 5MB limit');
        return;
      }
      setCertificationFile(file);
      setError('');
    }
  };

  const handleUploadCertification = async (e) => {
    e.preventDefault();
    if (!certificationFile) {
      setError('Please select a file to upload');
      return;
    }

    setUploadingCertification(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('certificationImage', certificationFile);

      const response = await axios.post(`${API_URL}/admin/upload-certification`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setCertificationImageUrl(response.data.data.certificationImageUrl);
        setSuccess('✓ Certification image uploaded successfully!');
        setCertificationFile(null);
        setShowCertificationUpload(false);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to upload certification image';
      setError(`✗ Error uploading certification image: ${errorMsg}`);
      console.error('Upload error:', err);
    } finally {
      setUploadingCertification(false);
    }
  };

  const handleDeleteCertification = async () => {
    if (!window.confirm('Are you sure you want to delete the certification image?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(`${API_URL}/admin/delete-certification`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setCertificationImageUrl(null);
        setSuccess('✓ Certification image deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete certification image');
    }
  };

  const handleLogout = () => { 
    localStorage.removeItem('adminToken'); 
    localStorage.removeItem('adminUser'); 
    navigate('/admin/login'); 
  };

  const renderFieldError = (fieldName) => {
    if (errors[fieldName]) {
      return <span className="field-error">⚠ {errors[fieldName]}</span>;
    }
    return null;
  };

  return (
    <div className="admin-dashboard">
      <SEO title="Admin Dashboard" path="/admin/dashboard" noindex />

      <header className="admin-header">
        <div className="admin-header-inner">
          <h1>Admin Dashboard</h1>
          <button className="danger-btn" onClick={handleLogout}><FaSignOutAlt /> Logout</button>
        </div>
      </header>

      <main className="admin-container">
        {success && <div className="success-box">✓ {success}</div>}
        {error && <div className="error-box">✗ {error}</div>}

        <div className="admin-actions">
          <button className="primary-btn" onClick={() => { setShowForm(!showForm); setShowEquipmentForm(false); setErrors({}); setError(''); }}><FaPlus /> {showForm ? 'Cancel' : 'Issue Certificate'}</button>
          <button className="secondary-btn equipment-action-btn" onClick={() => { setShowEquipmentForm(!showEquipmentForm); setShowForm(false); setShowCertificationUpload(false); setErrors({}); setError(''); }}><FaTools /> {showEquipmentForm ? 'Cancel' : 'Equipment Certification Creation'}</button>
          <button className="secondary-btn" onClick={() => { setShowCertificationUpload(!showCertificationUpload); setShowEquipmentForm(false); }}><FaImage /> {showCertificationUpload ? 'Cancel' : 'Company Certification'}</button>
        </div>

        {showCertificationUpload && (
          <div className="certification-upload-section">
            <h2><FaUpload /> Upload Company Certification</h2>
            <p className="section-description">Upload an image of your company's certification or accreditation</p>
            
            <form onSubmit={handleUploadCertification} className="certification-form">
              <div className="form-group">
                <label>Certification Image *</label>
                <div className="file-upload-area">
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleCertificationFileChange}
                    id="certFile"
                    style={{ display: 'none' }}
                    disabled={uploadingCertification}
                  />
                  <label htmlFor="certFile" className="file-upload-label">
                    <FaUpload className="upload-icon" />
                    {certificationFile ? certificationFile.name : 'Click to upload or drag and drop'}
                    <span className="upload-hint">PNG, JPG, GIF (max 5MB)</span>
                  </label>
                </div>
              </div>

              {certificationFile && (
                <div className="file-preview">
                  <p><strong>Selected:</strong> {certificationFile.name} ({(certificationFile.size / 1024).toFixed(2)} KB)</p>
                </div>
              )}

              <button 
                className="primary-btn" 
                type="submit" 
                disabled={!certificationFile || uploadingCertification}
              >
                <FaUpload /> {uploadingCertification ? 'Uploading...' : 'Upload Certification'}
              </button>
            </form>

            {certificationImageUrl && (
              <div className="certification-preview">
                <h3>Current Certification</h3>
                <img src={certificationImageUrl} alt="Company certification" className="cert-preview-image" />
                <button 
                  className="danger-btn" 
                  onClick={handleDeleteCertification}
                  style={{ marginTop: '1rem' }}
                >
                  <FaTrash /> Delete Certification
                </button>
              </div>
            )}
          </div>
        )}

        {showEquipmentForm && (
          <form onSubmit={handleEquipmentSubmit} className="issue-form equipment-cert-form">
            <h2><FaTools /> Equipment Certification Creation</h2>
            <p className="section-description">Create a verifiable equipment certificate record using the uploaded certificate image exactly as attached.</p>

            <div className="form-row">
              <div className="form-group">
                <label>Client * {renderFieldError('equipment.client')}</label>
                <input
                  name="client"
                  value={equipmentData.client}
                  onChange={handleEquipmentInputChange}
                  className={errors['equipment.client'] ? 'input-error' : ''}
                  required
                />
              </div>

              <div className="form-group">
                <label>Recipient Email {renderFieldError('equipment.recipientEmail')}</label>
                <input
                  name="recipientEmail"
                  type="email"
                  value={equipmentData.recipientEmail}
                  onChange={handleEquipmentInputChange}
                  placeholder="Optional verification email"
                  className={errors['equipment.recipientEmail'] ? 'input-error' : ''}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Client Location * {renderFieldError('equipment.clientLocation')}</label>
                <input
                  name="clientLocation"
                  value={equipmentData.clientLocation}
                  onChange={handleEquipmentInputChange}
                  className={errors['equipment.clientLocation'] ? 'input-error' : ''}
                  required
                />
              </div>

              <div className="form-group">
                <label>Location of Tests * {renderFieldError('equipment.testLocation')}</label>
                <input
                  name="testLocation"
                  value={equipmentData.testLocation}
                  onChange={handleEquipmentInputChange}
                  className={errors['equipment.testLocation'] ? 'input-error' : ''}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Item Description * {renderFieldError('equipment.itemDescription')}</label>
                <input
                  name="itemDescription"
                  value={equipmentData.itemDescription}
                  onChange={handleEquipmentInputChange}
                  className={errors['equipment.itemDescription'] ? 'input-error' : ''}
                  required
                />
              </div>

              <div className="form-group">
                <label>ID No * {renderFieldError('equipment.equipmentId')}</label>
                <input
                  name="equipmentId"
                  value={equipmentData.equipmentId}
                  onChange={handleEquipmentInputChange}
                  className={errors['equipment.equipmentId'] ? 'input-error' : ''}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Certificate Number * {renderFieldError('equipment.certificateNumber')}</label>
                <input
                  name="certificateNumber"
                  value={equipmentData.certificateNumber}
                  onChange={handleEquipmentInputChange}
                  className={errors['equipment.certificateNumber'] ? 'input-error' : ''}
                  placeholder="e.g., DOL/NAT/AM/01"
                  required
                />
                <small>This becomes the certificate ID for verification. {CERTIFICATE_ID_HINT}, e.g. DOL/TAT/BM/05 or DOL/LIFT/INS/2026-01.</small>
              </div>

              <div className="form-group">
                <label>Max Working Pressure * {renderFieldError('equipment.maxWorkingPressure')}</label>
                <input
                  name="maxWorkingPressure"
                  value={equipmentData.maxWorkingPressure}
                  onChange={handleEquipmentInputChange}
                  className={errors['equipment.maxWorkingPressure'] ? 'input-error' : ''}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Temperature</label>
                <input
                  name="temperature"
                  value={equipmentData.temperature}
                  onChange={handleEquipmentInputChange}
                  placeholder="28.5 +/-2"
                />
              </div>

              <div className="form-group">
                <label>Relative Humidity</label>
                <input
                  name="relativeHumidity"
                  value={equipmentData.relativeHumidity}
                  onChange={handleEquipmentInputChange}
                  placeholder="68% +5%"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Equipment Certification Image * {renderFieldError('equipment.certificateImage')}</label>
              <div className="file-upload-area">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleEquipmentImageChange}
                  id="equipmentImageAttachment"
                  style={{ display: 'none' }}
                />
                <label htmlFor="equipmentImageAttachment" className="file-upload-label equipment-file-upload-label">
                  <FaImage className="upload-icon" />
                  {equipmentImageAttachment ? equipmentImageAttachment.name : 'Attach equipment certification image'}
                  <span className="upload-hint">PNG, JPG, GIF (max 5MB)</span>
                </label>
              </div>
              {equipmentImageAttachment && (
                <div className="file-preview equipment-attachment-preview">
                  <p><strong>Selected:</strong> {equipmentImageAttachment.name} ({(equipmentImageAttachment.size / 1024).toFixed(2)} KB)</p>
                  <button className="secondary-btn" type="button" onClick={() => setEquipmentImageAttachment(null)}>
                    Remove Image
                  </button>
                </div>
              )}
            </div>

            <div className="equipment-test-table">
              <h3>Test Details</h3>
              {equipmentData.testRows.map((row, index) => (
                <div className="equipment-test-row" key={`equipment-test-${index}`}>
                  <div className="equipment-row-number">{index + 1}</div>
                  <div className="form-group">
                    <label>Description</label>
                    <input
                      value={row.description}
                      onChange={(e) => handleEquipmentRowChange(index, 'description', e.target.value)}
                      className={errors[`equipment.row${index}`] ? 'input-error' : ''}
                    />
                  </div>
                  <div className="form-group">
                    <label>Observation</label>
                    <input
                      value={row.observation}
                      onChange={(e) => handleEquipmentRowChange(index, 'observation', e.target.value)}
                      className={errors[`equipment.row${index}`] ? 'input-error' : ''}
                    />
                  </div>
                  <div className="form-group">
                    <label>Remarks</label>
                    <input
                      value={row.remarks}
                      onChange={(e) => handleEquipmentRowChange(index, 'remarks', e.target.value)}
                      className={errors[`equipment.row${index}`] ? 'input-error' : ''}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date Tested/Calibrated * {renderFieldError('equipment.testedDate')}</label>
                <input
                  name="testedDate"
                  type="date"
                  value={equipmentData.testedDate}
                  onChange={handleEquipmentInputChange}
                  className={errors['equipment.testedDate'] ? 'input-error' : ''}
                  required
                />
              </div>

              <div className="form-group">
                <label>Next Test/Calibration Date * {renderFieldError('equipment.nextTestDate')}</label>
                <input
                  name="nextTestDate"
                  type="date"
                  value={equipmentData.nextTestDate}
                  onChange={handleEquipmentInputChange}
                  className={errors['equipment.nextTestDate'] ? 'input-error' : ''}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Validity * {renderFieldError('equipment.validity')}</label>
                <input
                  name="validity"
                  value={equipmentData.validity}
                  onChange={handleEquipmentInputChange}
                  className={errors['equipment.validity'] ? 'input-error' : ''}
                  required
                />
              </div>

              <div className="form-group">
                <label>Procedure No * {renderFieldError('equipment.procedureNo')}</label>
                <input
                  name="procedureNo"
                  value={equipmentData.procedureNo}
                  onChange={handleEquipmentInputChange}
                  className={errors['equipment.procedureNo'] ? 'input-error' : ''}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Reference Standard * {renderFieldError('equipment.referenceStandard')}</label>
              <input
                name="referenceStandard"
                value={equipmentData.referenceStandard}
                onChange={handleEquipmentInputChange}
                className={errors['equipment.referenceStandard'] ? 'input-error' : ''}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Inspector Name</label>
                <input
                  name="inspectorName"
                  value={equipmentData.inspectorName}
                  onChange={handleEquipmentInputChange}
                />
              </div>

              <div className="form-group">
                <label>Inspector Credential</label>
                <input
                  name="inspectorCredential"
                  value={equipmentData.inspectorCredential}
                  onChange={handleEquipmentInputChange}
                />
              </div>
            </div>

            <button className="primary-btn" type="submit" disabled={creatingEquipmentCertificate}>
              <FaTools /> {creatingEquipmentCertificate ? 'Creating...' : 'Create Equipment Certification'}
            </button>
          </form>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="issue-form">
            <h2>Issue New Certificate</h2>

            <div className="recipient-rows">
              <h3>Recipients ({recipients.length})</h3>
              <p className="section-description">
                Each recipient receives their own certificate record with a unique certificate ID.
                The shared certificate details below apply to every recipient in this batch.
              </p>

              {recipients.map((recipient, index) => (
                <div className="recipient-row" key={`recipient-${index}`}>
                  <div className="equipment-row-number recipient-row-number">{index + 1}</div>
                  <div className="form-group">
                    <label>Certificate ID * {renderFieldError(`recipient.${index}.certificateId`)}</label>
                    <input
                      value={recipient.certificateId}
                      onChange={(e) => handleRecipientChange(index, 'certificateId', e.target.value)}
                      placeholder="e.g., DOF/CP/1226-01"
                      className={errors[`recipient.${index}.certificateId`] ? 'input-error' : ''}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Recipient Name * {renderFieldError(`recipient.${index}.recipientName`)}</label>
                    <input
                      value={recipient.recipientName}
                      onChange={(e) => handleRecipientChange(index, 'recipientName', e.target.value)}
                      placeholder="Full name of the certificate recipient"
                      className={errors[`recipient.${index}.recipientName`] ? 'input-error' : ''}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Recipient Email * {renderFieldError(`recipient.${index}.recipientEmail`)}</label>
                    <input
                      type="email"
                      value={recipient.recipientEmail}
                      onChange={(e) => handleRecipientChange(index, 'recipientEmail', e.target.value)}
                      placeholder="recipient@example.com"
                      className={errors[`recipient.${index}.recipientEmail`] ? 'input-error' : ''}
                      required
                    />
                  </div>
                  <button
                    className="secondary-btn recipient-remove-btn"
                    type="button"
                    onClick={() => removeRecipient(index)}
                    disabled={recipients.length === 1 || creatingCertificates}
                    title="Remove recipient"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}

              <small>{CERTIFICATE_ID_HINT}. Examples: DOF/CP/1226-01, DOF/TR/2026-001, DOF/SAFETY/2026-15.</small>

              <div className="recipient-rows-actions">
                <button className="secondary-btn" type="button" onClick={addRecipient} disabled={creatingCertificates}>
                  <FaPlus /> Add Another Recipient
                </button>
              </div>
            </div>

            <h3 className="shared-details-heading">Shared Certificate Details</h3>

            <div className="form-group">
              <label>Certificate Title * {renderFieldError('certificateTitle')}</label>
              <input 
                name="certificateTitle" 
                value={formData.certificateTitle} 
                onChange={handleInputChange}
                placeholder="e.g., Mobile Crane Operator Certification"
                className={errors.certificateTitle ? 'input-error' : ''}
                required 
              />
            </div>

            <div className="form-group">
              <label>Description {renderFieldError('description')}</label>
              <textarea 
                name="description" 
                value={formData.description} 
                onChange={handleInputChange}
                placeholder="Certificate description (min 5 characters)"
                className={errors.description ? 'input-error' : ''}
                rows="3"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Certificate Type</label>
                <select 
                  name="certificateType" 
                  value={formData.certificateType}
                  onChange={handleInputChange}
                >
                  <option value="Course Completion">Course Completion</option>
                  <option value="Achievement">Achievement</option>
                  <option value="Certification">Certification</option>
                  <option value="Award">Award</option>
                  <option value="Training">Training</option>
                  <option value="Training Certification">Training Certification</option>
                  <option value="Professional Certification">Professional Certification</option>
                  <option value="Equipment Certification">Equipment Certification</option>
                </select>
              </div>

              <div className="form-group">
                <label>Grade</label>
                <select 
                  name="grade" 
                  value={formData.grade}
                  onChange={handleInputChange}
                >
                  <option value="A+">A+</option>
                  <option value="A">A</option>
                  <option value="B+">B+</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Issued Date * {renderFieldError('issuedDate')}</label>
                <input 
                  name="issuedDate" 
                  type="date"
                  value={formData.issuedDate} 
                  onChange={handleInputChange}
                  className={errors.issuedDate ? 'input-error' : ''}
                  required 
                />
              </div>

              <div className="form-group">
                <label>Expiry Date {renderFieldError('expiryDate')}</label>
                <input 
                  name="expiryDate" 
                  type="date"
                  value={formData.expiryDate} 
                  onChange={handleInputChange}
                  className={errors.expiryDate ? 'input-error' : ''}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Skills (comma-separated)</label>
              <input 
                name="skills" 
                value={formData.skills} 
                onChange={handleInputChange}
                placeholder="e.g., Crane Operation, Load Management, Safety Protocols"
              />
            </div>

            <div className="form-group">
              <label>Certificate Image/Barcode {renderFieldError('certificateImage')}</label>
              <input 
                type="file" 
                accept="image/*"
                onChange={handleImageChange}
                className={errors.certificateImage ? 'input-error' : ''}
              />
              {certificateImageFile && (
                <div className="image-preview">
                  <p><strong>Selected:</strong> {certificateImageFile.name} ({(certificateImageFile.size / 1024).toFixed(2)} KB)</p>
                </div>
              )}
            </div>

            <button className="primary-btn" type="submit" disabled={creatingCertificates}>
              <FaPlus /> {creatingCertificates
                ? `Creating ${recipients.length > 1 ? `${recipients.length} certificates` : 'certificate'}...`
                : (recipients.length > 1 ? `Create ${recipients.length} Certificates` : 'Create Certificate')}
            </button>
          </form>
        )}

        <section className="issued-list">
          <h2><FaList /> Issued Certificates ({certificates.length})</h2>
          {loading ? <p>Loading...</p> : certificates.length === 0 ? <p className="empty-box">No certificates issued yet</p> : (
            <table className="cert-table">
              <thead>
                <tr>
                  <th>Certificate ID</th>
                  <th>Recipient</th>
                  <th>Title</th>
                  <th>Issued Date</th>
                  <th>Grade</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {certificates.map(cert => (
                  <tr key={cert._id}>
                    <td data-label="Certificate ID">{cert.certificateId}</td>
                    <td data-label="Recipient">{cert.recipientName}</td>
                    <td data-label="Title">{cert.certificateTitle}</td>
                    <td data-label="Issued Date">{new Date(cert.issuedDate).toLocaleDateString()}</td>
                    <td data-label="Grade">{cert.grade}</td>
                    <td data-label="Actions">
                      <button 
                        className="secondary-btn" 
                        onClick={() => handleViewCertificate(cert)}
                        title="View certificate"
                        type="button"
                      >
                        <FaEye />
                      </button>
                      <button
                        className="secondary-btn edit-btn"
                        onClick={() => handleEditCertificate(cert)}
                        title="Edit certificate"
                        type="button"
                      >
                        <FaEdit /> Edit
                      </button>
                      <button 
                        className="secondary-btn print-btn" 
                        onClick={() => handlePrintCertificate(cert)}
                        title="Print or save certificate"
                        type="button"
                      >
                        <FaPrint /> Print / Save
                      </button>
                      <button
                        className="secondary-btn download-btn"
                        onClick={() => downloadCertificateImage(cert, 'png')}
                        title="Download certificate as PNG"
                        type="button"
                        disabled={!cert.certificateImage}
                      >
                        <FaDownload /> PNG
                      </button>
                      <button
                        className="secondary-btn download-btn"
                        onClick={() => downloadCertificateImage(cert, 'jpeg')}
                        title="Download certificate as JPEG"
                        type="button"
                        disabled={!cert.certificateImage}
                      >
                        <FaDownload /> JPEG
                      </button>
                      <button 
                        className="danger-btn" 
                        onClick={() => handleDelete(cert._id)}
                        title="Delete certificate"
                        type="button"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>

      {showEditForm && editingCert && (
        <div className="modal-overlay" onClick={closeEditForm}>
          <div className="modal-content edit-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" type="button" onClick={closeEditForm}>×</button>
            <h2><FaEdit /> Edit Certificate</h2>
            <form onSubmit={handleEditSubmit} className="issue-form edit-certificate-form">
              <div className="form-group">
                <label>Certificate ID * {renderFieldError('editCertificateId')}</label>
                <input
                  name="certificateId"
                  value={editFormData.certificateId}
                  onChange={handleEditInputChange}
                  className={errors.editCertificateId ? 'input-error' : ''}
                  required
                />
                <small>{CERTIFICATE_ID_HINT}.</small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Recipient Name * {renderFieldError('editRecipientName')}</label>
                  <input
                    name="recipientName"
                    value={editFormData.recipientName}
                    onChange={handleEditInputChange}
                    className={errors.editRecipientName ? 'input-error' : ''}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Recipient Email {renderFieldError('editRecipientEmail')}</label>
                  <input
                    name="recipientEmail"
                    type="email"
                    value={editFormData.recipientEmail}
                    onChange={handleEditInputChange}
                    className={errors.editRecipientEmail ? 'input-error' : ''}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Certificate Title * {renderFieldError('editCertificateTitle')}</label>
                <input
                  name="certificateTitle"
                  value={editFormData.certificateTitle}
                  onChange={handleEditInputChange}
                  className={errors.editCertificateTitle ? 'input-error' : ''}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description {renderFieldError('editDescription')}</label>
                <textarea
                  name="description"
                  value={editFormData.description}
                  onChange={handleEditInputChange}
                  className={errors.editDescription ? 'input-error' : ''}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Certificate Type</label>
                  <select
                    name="certificateType"
                    value={editFormData.certificateType}
                    onChange={handleEditInputChange}
                  >
                    <option value="Course Completion">Course Completion</option>
                    <option value="Achievement">Achievement</option>
                    <option value="Certification">Certification</option>
                    <option value="Award">Award</option>
                    <option value="Training">Training</option>
                    <option value="Training Certification">Training Certification</option>
                    <option value="Professional Certification">Professional Certification</option>
                    <option value="Equipment Certification">Equipment Certification</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Grade</label>
                  <input
                    name="grade"
                    value={editFormData.grade}
                    onChange={handleEditInputChange}
                    placeholder="e.g., A or SATISFACTORY"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Issued Date * {renderFieldError('editIssuedDate')}</label>
                  <input
                    name="issuedDate"
                    type="date"
                    value={editFormData.issuedDate}
                    onChange={handleEditInputChange}
                    className={errors.editIssuedDate ? 'input-error' : ''}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Expiry Date {renderFieldError('editExpiryDate')}</label>
                  <input
                    name="expiryDate"
                    type="date"
                    value={editFormData.expiryDate}
                    onChange={handleEditInputChange}
                    className={errors.editExpiryDate ? 'input-error' : ''}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Skills (comma-separated)</label>
                <input
                  name="skills"
                  value={editFormData.skills}
                  onChange={handleEditInputChange}
                />
              </div>

              <div className="form-group">
                <label>Replace Certificate Image</label>
                {editingCert.certificateImage && (
                  <div className="edit-current-image">
                    <img src={editingCert.certificateImage} alt="Current certificate" />
                    <span>Current image will remain unless a new image is selected.</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleEditImageChange}
                />
                {editImageFile && (
                  <div className="image-preview">
                    <p><strong>Selected:</strong> {editImageFile.name} ({(editImageFile.size / 1024).toFixed(2)} KB)</p>
                  </div>
                )}
              </div>

              <div className="edit-form-actions">
                <button className="primary-btn" type="submit" disabled={updatingCertificate}>
                  <FaEdit /> {updatingCertificate ? 'Saving...' : 'Save Changes'}
                </button>
                <button className="secondary-btn" type="button" onClick={closeEditForm} disabled={updatingCertificate}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreview && previewCert && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPreview(false)}>×</button>
            <h2>Certificate Preview</h2>
            <div className="certificate-preview">
              {previewCert.certificateImage ? (
                <div className="cert-image-wrapper">
                  <img src={previewCert.certificateImage} alt="Certificate" className="cert-image" />
                  {previewCert.qrCode && (
                    <div className="cert-qr-overlay">
                      <img src={previewCert.qrCode} alt="Certificate QR code" className="cert-qr-image" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="cert-placeholder">
                  <p>No image available</p>
                </div>
              )}
            </div>
            <div className="cert-details">
              <p><strong>Certificate ID:</strong> {previewCert.certificateId}</p>
              <p><strong>Recipient:</strong> {previewCert.recipientName}</p>
              <p><strong>Title:</strong> {previewCert.certificateTitle}</p>
              <p><strong>Type:</strong> {previewCert.certificateType}</p>
              <p><strong>Grade:</strong> {previewCert.grade}</p>
              <p><strong>Issued:</strong> {new Date(previewCert.issuedDate).toLocaleDateString()}</p>
              {previewCert.expiryDate && (
                <p><strong>Expires:</strong> {new Date(previewCert.expiryDate).toLocaleDateString()}</p>
              )}
              {previewCert.skills && previewCert.skills.length > 0 && (
                <p><strong>Skills:</strong> {previewCert.skills.join(', ')}</p>
              )}
            </div>
            <div className="cert-preview-actions">
              <button className="primary-btn" type="button" onClick={() => handlePrintCertificate(previewCert)}>
                <FaPrint /> Print / Save
              </button>
              <button
                className="secondary-btn download-btn"
                type="button"
                onClick={() => downloadCertificateImage(previewCert, 'png')}
                disabled={!previewCert.certificateImage}
              >
                <FaDownload /> Download PNG
              </button>
              <button
                className="secondary-btn download-btn"
                type="button"
                onClick={() => downloadCertificateImage(previewCert, 'jpeg')}
                disabled={!previewCert.certificateImage}
              >
                <FaDownload /> Download JPEG
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrintPreview && printCert && (
        <div className="print-certificate-modal">
          <div className="print-certificate-panel">
            <div className="print-certificate-topbar">
              <div className="print-export-actions">
                <button className="primary-btn" type="button" onClick={handlePrintCurrentCertificate}>
                  <FaPrint /> Print / Save PDF
                </button>
                <button className="secondary-btn print-export-btn" type="button" onClick={() => exportPrintCertificateImage('png')}>
                  <FaDownload /> Save PNG
                </button>
                <button className="secondary-btn print-export-btn" type="button" onClick={() => exportPrintCertificateImage('jpeg')}>
                  <FaDownload /> Save JPEG
                </button>
              </div>
              <button className="modal-close print-close" type="button" onClick={() => { setShowPrintPreview(false); setPrintCert(null); }}>×</button>
            </div>
            <div className="certificate-print-sheet">
              {printCert.certificateImage ? (
                <>
                  <img src={printCert.certificateImage} alt="Certificate" className="print-certificate-image" />
                  <div className="print-qr-verification">
                    <div className="print-qr-overlay">
                      <img src={getCertificatePrintQrUrl(printCert)} alt="Verification QR code" className="print-qr-image" />
                    </div>
                    <div className="print-qr-text">
                      SCAN QR CODE
                      <span>FOR VERIFICATION</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="print-certificate-fallback">
                  <h2>{printCert.certificateTitle || 'Certificate of Competence'}</h2>
                  <p><strong>Certificate No:</strong> {printCert.certificateId}</p>
                  <p><strong>Recipient:</strong> {printCert.recipientName}</p>
                  <p><strong>Issued:</strong> {new Date(printCert.issuedDate).toLocaleDateString()}</p>
                  <p><strong>Type:</strong> {printCert.certificateType}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
